import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import axios from 'axios';
import ShaderShowcase from '@/components/ui/hero';
import { PricingCard } from '@/components/ui/animated-glassy-pricing';
import { KpiCard } from '@/components/ui/kpi-card';
import { Facebook, Instagram, Search, Video } from 'lucide-react';

/* ─── Colour tokens ─────────────────────────────────────────────────── */
const C = {
  bg:          '#0F1C2E',
  bgWarm:      '#14243A',
  bgSection:   '#111F33',
  bgLight:     '#F7E7C6',
  bgParchment: '#FFF5DE',
  magenta:     '#7D468C',
  magentaHot:  '#9A5CAD',
  gold:        '#4EBFBF',
  orange:      '#F2785C',
  verde:       '#2F7D4E',
  verdeLight:  '#58A96F',
  cream:       '#FFF8EA',
  creamMid:    '#E8CFA2',
  charcoal:    '#233559',
  text:        '#FFF8EA',
  masa:        '#F7E7C6',
  salsa:       '#6E1F1A',
  cilantro:    '#2F7D4E',
};

const WHATSAPP_URL = 'https://wa.me/+17874222734';
const PLAN_PDF_FILENAME = 'LaGacha_Propuesta_MikelMedia.pdf';

/* ─── Responsive CSS ────────────────────────────────────────────────── */
const responsiveCss = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&family=Source+Sans+Pro:wght@400;600;700&display=swap');

  .lg-page * { box-sizing: border-box; }
  .lg-page a, .lg-page button { -webkit-tap-highlight-color: transparent; }
  .lg-container { max-width: 1280px; margin: 0 auto; padding: 0 48px; }
  .lg-section { padding: 120px 0; }
  .lg-hero { min-height: 100svh; }
  .lg-hero-photo { transform: scale(1.04); animation: lg-photo-breathe 18s ease-in-out infinite alternate; }
  .lg-hero-content { position: relative; z-index: 5; padding: 0 48px; max-width: 1280px; margin: 0 auto; width: 100%; }
  .lg-hero-logo { position: absolute; top: 40px; left: 48px; z-index: 10; }
  .lg-hero-badge { position: absolute; top: 40px; right: 48px; z-index: 10; }
  .lg-stats-strip { position: relative; z-index: 5; margin-top: 54px; border-top: 1px solid rgba(255,248,234,0.22); display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); background: rgba(11,6,1,0.58); backdrop-filter: blur(18px); }
  .lg-stat-chip { min-width: 0; }
  .lg-platform-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
  .lg-compare-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
  .lg-pkg-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .lg-phase-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; }
  .lg-kpi-grid { display: grid; grid-template-columns: 1fr; gap: 20px; max-width: 800px; }
  .lg-steps-grid { display: grid; grid-template-columns: 56px 1fr; gap: 20px 28px; align-items: start; }
  .lg-opp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  .lg-two-col { display: grid; grid-template-columns: 1fr 1.15fr; gap: 80px; align-items: start; }
  .lg-cta-row { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; }
  .lg-button { min-height: 52px; display: inline-flex !important; align-items: center; justify-content: center; text-align: center; }
  .lg-hero-kicker { animation: lg-slide-in-left 0.8s ease both 0.08s; }
  .lg-hero-title { animation: lg-slide-in-left 0.95s ease both 0.18s; }
  .lg-hero-copy { animation: lg-slide-in-left 0.95s ease both 0.32s; }
  .lg-hero-actions { animation: lg-counter-up 0.85s ease both 0.48s; }
  .lg-final-cta { padding: 60px 48px; background: linear-gradient(135deg, #233559 0%, #0F1C2E 58%, #6E1F1A 140%); border-top: 1px solid rgba(242,120,92,0.32); display: flex; align-items: center; justify-content: space-between; gap: 32px; }
  .lg-footer { padding: 32px 48px; background: ${C.bg}; border-top: 1px solid rgba(255,255,255,0.06); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }

  @keyframes lg-float { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-8px) rotate(2deg); } }
  @keyframes lg-pulse-glow { 0%,100% { box-shadow: 0 0 0 0 rgba(201,0,94,0); } 50% { box-shadow: 0 0 32px 8px rgba(201,0,94,0.25); } }
  @keyframes lg-slide-in-left { from { opacity:0; transform:translateX(-40px); } to { opacity:1; transform:translateX(0); } }
  @keyframes lg-counter-up { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes lg-bar-fill { from { width: 0; } to { width: var(--bar-target); } }
  @keyframes lg-border-glow { 0%,100% { border-color: rgba(201,0,94,0.4); } 50% { border-color: rgba(238,17,117,0.85); } }
  @keyframes lg-photo-breathe { from { transform: scale(1.04) translate3d(0,0,0); } to { transform: scale(1.1) translate3d(-1.5%, -1%, 0); } }

  @media (max-width: 1024px) {
    .lg-container { padding: 0 32px; }
    .lg-section { padding: 88px 0; }
    .lg-stats-strip { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .lg-platform-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .lg-pkg-grid { grid-template-columns: 1fr !important; max-width: 480px; margin: 0 auto; }
    .lg-phase-grid { grid-template-columns: 1fr !important; }
    .lg-two-col { grid-template-columns: 1fr !important; gap: 56px !important; }
    .lg-compare-grid { grid-template-columns: 1fr !important; }
    .lg-opp-grid { grid-template-columns: 1fr !important; }
  }

  @media (max-width: 700px) {
    .lg-section { padding: 68px 0; }
    .lg-container { padding: 0 20px; }
    .lg-hero { min-height: auto !important; padding-top: 112px; }
    .lg-hero-content { padding: 0 20px !important; }
    .lg-hero-logo { top: 22px !important; left: 20px !important; }
    .lg-hero-badge { top: 64px !important; left: 20px !important; right: auto !important; max-width: calc(100vw - 40px); padding: 7px 14px !important; font-size: 9px !important; }
    .lg-stats-strip { display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)); overflow: visible; margin-top: 40px; }
    .lg-stat-chip { min-width: 0; padding: 18px 12px !important; }
    .lg-platform-grid { grid-template-columns: 1fr !important; }
    .lg-pkg-grid { grid-template-columns: 1fr !important; }
    .lg-phase-grid { grid-template-columns: 1fr !important; }
    .lg-opp-grid { grid-template-columns: 1fr !important; }
    .lg-compare-grid { grid-template-columns: 1fr !important; }
    .lg-two-col { grid-template-columns: 1fr !important; gap: 40px !important; }
    .lg-cta-row { flex-direction: column; align-items: stretch; }
    .lg-button { width: 100% !important; }
    .lg-final-cta { padding: 44px 20px !important; flex-direction: column; align-items: stretch; }
    .lg-footer { padding: 28px 20px !important; justify-content: center !important; text-align: center; }
  }

  @media (prefers-reduced-motion: reduce) {
    .lg-page *, .lg-page *::before, .lg-page *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      scroll-behavior: auto !important;
      transition-duration: 0.001ms !important;
    }
    .lg-page canvas { display: none; }
  }
`;

/* ─── Style helpers ─────────────────────────────────────────────────── */
const css = {
  page: {
    fontFamily: "'Source Sans Pro', Arial, sans-serif",
    background: C.bg,
    color: C.cream,
    overflowX: 'hidden',
  },
  display: {
    fontFamily: "'Poppins', Verdana, sans-serif",
    fontWeight: 800,
  },
  number: {
    fontFamily: "'Poppins', Verdana, sans-serif",
    fontWeight: 800,
    letterSpacing: 0,
  },
  eyebrow: {
    fontFamily: "'Poppins', Verdana, sans-serif",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: C.magenta,
  },
  label: {
    fontFamily: "'Poppins', Verdana, sans-serif",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
  },
};

/* ─── Intersection observer hook ────────────────────────────────────── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ─── Count-up hook ─────────────────────────────────────────────────── */
function useCountUp(target, duration = 1800, trigger) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger || target === 0) {
      if (trigger && target === 0) setCount(0);
      return;
    }
    let current = 0;
    const steps = Math.ceil(duration / 16);
    const increment = target / steps;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, trigger]);
  return count;
}

/* ─── Reveal wrapper ────────────────────────────────────────────────── */
function Reveal({ children, delay = 0, style = {}, from = 'bottom' }) {
  const [ref, visible] = useReveal();
  const tx = from === 'left' ? '-32px' : from === 'right' ? '32px' : '0';
  const ty = from === 'bottom' ? '32px' : '0';
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translate(0,0)' : `translate(${tx},${ty})`,
      transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ─── Section wrapper ───────────────────────────────────────────────── */
function Section({ children, bg = C.bg, style = {}, id }) {
  return (
    <section id={id} className="lg-section" style={{ background: bg, position: 'relative', overflow: 'hidden', ...style }}>
      <div className="lg-container">{children}</div>
    </section>
  );
}

/* ─── Chili/confetti particles ──────────────────────────────────────── */
function FiestaParticles({ canvasRef }) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false });
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, canvas.clientWidth / canvas.clientHeight, 0.1, 1000);
    camera.position.z = 90;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);

    const COUNT = 900;
    const positions = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);
    const speeds = new Float32Array(COUNT);
    const drift = new Float32Array(COUNT);

    const palette = [
      new THREE.Color(C.magenta),
      new THREE.Color(C.gold),
      new THREE.Color(C.orange),
      new THREE.Color(C.verde),
      new THREE.Color(C.cream),
    ];

    for (let i = 0; i < COUNT; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * 220;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 130;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 70;
      const col = palette[Math.floor(Math.random() * palette.length)];
      colors[i * 3] = col.r; colors[i * 3 + 1] = col.g; colors[i * 3 + 2] = col.b;
      speeds[i] = 0.005 + Math.random() * 0.016;
      drift[i]  = (Math.random() - 0.5) * 0.008;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color',    new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.PointsMaterial({ size: 0.75, vertexColors: true, transparent: true, opacity: 0.65, sizeAttenuation: true });
    const points = new THREE.Points(geo, mat);
    scene.add(points);

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const pos = geo.attributes.position.array;
      for (let i = 0; i < COUNT; i++) {
        pos[i * 3 + 1] += speeds[i];
        pos[i * 3]     += drift[i];
        if (pos[i * 3 + 1] > 67) pos[i * 3 + 1] = -67;
      }
      geo.attributes.position.needsUpdate = true;
      points.rotation.z += 0.0002;
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      camera.aspect = w / h; camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); renderer.dispose(); };
  }, [canvasRef]);
  return null;
}

/* ─── Stat chip ─────────────────────────────────────────────────────── */
function StatChip({ label, value, accent = C.gold, alert = false }) {
  return (
    <div className="lg-stat-chip" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      padding: '22px 24px', gap: 5,
      borderLeft: `1px solid rgba(255,248,234,0.12)`,
    }}>
      <span style={{ ...css.label, color: 'rgba(255,248,234,0.5)', fontSize: 9 }}>{label}</span>
      <span style={{ ...css.number, fontSize: 34, color: alert ? C.orange : accent, lineHeight: 1 }}>{value}</span>
      {alert && <span style={{ fontSize: 9, color: C.orange, letterSpacing: '0.2em', fontWeight: 700, textTransform: 'uppercase' }}>Prioridad</span>}
    </div>
  );
}

/* ─── Platform card with animated counter ───────────────────────────── */
function PlatformCard({ platform, icon, stat, statLabel, sub, accent = C.gold, alert = false, badge }) {
  const [ref, visible] = useReveal();
  const num = parseInt(stat.replace(/[^0-9]/g, ''), 10) || 0;
  const counted = useCountUp(num, 1600, visible);
  const displayStat = num === 0 ? stat : stat.replace(/[\d,]+/, counted.toLocaleString());

  return (
    <div ref={ref} style={{
      background: alert ? 'rgba(232,98,26,0.05)' : 'rgba(11,6,1,0.03)',
      borderTop: `1px solid ${alert ? 'rgba(232,98,26,0.42)' : 'rgba(11,6,1,0.14)'}`,
      padding: '34px 0',
      position: 'relative',
      overflow: 'hidden',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: 'opacity 0.7s ease, transform 0.7s ease',
    }}>
      {alert && (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: 68, height: 3,
          background: `linear-gradient(90deg, ${C.orange}, ${C.magenta})`,
        }} />
      )}
      {badge && (
        <div style={{
          display: 'inline-flex',
          background: alert ? 'rgba(232,98,26,0.12)' : 'rgba(201,0,94,0.12)', color: alert ? C.orange : C.magenta,
          fontSize: 9, fontWeight: 700, letterSpacing: '0.18em',
          textTransform: 'uppercase', padding: '5px 9px',
          marginBottom: 14,
        }}>
          {badge}
        </div>
      )}
      <div style={{
        width: 42, height: 42, borderRadius: 999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${accent}45`, color: accent,
        fontSize: 18, marginBottom: 16,
      }}>{icon}</div>
      <div style={{ ...css.label, color: 'rgba(255,248,234,0.45)', marginBottom: 10 }}>{platform}</div>
      <div style={{ ...css.number, fontSize: 48, color: alert ? C.orange : accent, lineHeight: 1, marginBottom: 6 }}>
        {displayStat}
      </div>
      <div style={{ fontSize: 13, color: 'rgba(255,248,234,0.65)', marginBottom: alert && sub ? 10 : 0 }}>{statLabel}</div>
      {sub && <div style={{ fontSize: 12, color: alert ? C.orange : 'rgba(255,248,234,0.4)', marginTop: 8, lineHeight: 1.5 }}>{sub}</div>}
    </div>
  );
}

/* ─── Opportunity card ──────────────────────────────────────────────── */
function OppCard({ icon, tag, title, body, accent }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      background: 'transparent',
      borderTop: `1px solid ${accent}55`,
      padding: '30px 0 0',
      position: 'relative',
      overflow: 'hidden',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(28px)',
      transition: 'opacity 0.75s ease, transform 0.75s ease, border-color 0.3s',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = accent}
      onMouseLeave={e => e.currentTarget.style.borderColor = `${accent}40`}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 999, marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${accent}14`, color: accent, border: `1px solid ${accent}35`,
        fontSize: 19,
      }}>{icon}</div>
      <div style={{ ...css.eyebrow, color: accent, marginBottom: 14 }}>{tag}</div>
      <h3 style={{ ...css.display, fontSize: 26, color: C.cream, margin: '0 0 16px', lineHeight: 1.2 }}>{title}</h3>
      <p style={{ fontSize: 15, lineHeight: 1.75, color: 'rgba(255,248,234,0.7)', margin: 0 }}>{body}</p>
    </div>
  );
}

/* ─── Package card ──────────────────────────────────────────────────── */
function PkgCard({ id, name, price, monthlyTag, features, platforms, extras, onboarding, recommended, selected, onSelect, accent }) {
  const isSelected = selected === id;
  return (
    <div
      onClick={() => onSelect(id)}
      style={{
        background: isSelected ? `linear-gradient(160deg, ${accent}18 0%, rgba(11,6,1,0.95) 100%)` : 'rgba(255,255,255,0.025)',
        border: `2px solid ${isSelected ? accent : 'rgba(255,255,255,0.08)'}`,
        padding: '40px 32px',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.35s cubic-bezier(0.23,1,0.32,1)',
        transform: isSelected ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: isSelected ? `0 20px 60px ${accent}30, 0 4px 20px rgba(0,0,0,0.4)` : '0 2px 12px rgba(0,0,0,0.3)',
      }}
    >
      {recommended && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          background: `linear-gradient(90deg, ${C.magenta}, ${C.magentaHot})`,
          textAlign: 'center', padding: '7px',
          ...css.label, color: C.cream, fontSize: 10, letterSpacing: '0.25em',
        }}>
          RECOMENDADO ★
        </div>
      )}
      <div style={{ paddingTop: recommended ? 28 : 0 }}>
        <div style={{ ...css.eyebrow, color: accent, marginBottom: 12 }}>{id.toUpperCase()}</div>
        <h3 style={{ ...css.display, fontSize: 30, color: C.cream, margin: '0 0 4px' }}>{name}</h3>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 28 }}>
          <span style={{ ...css.number, fontSize: 54, color: accent, lineHeight: 1 }}>${price}</span>
          <span style={{ fontSize: 13, color: 'rgba(255,248,234,0.5)' }}>/ {monthlyTag}</span>
        </div>

        <div style={{ borderTop: `1px solid rgba(255,255,255,0.08)`, paddingTop: 24, marginBottom: 24 }}>
          {features.map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
              <span style={{ color: accent, fontSize: 13, marginTop: 2, flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: 14, color: 'rgba(255,248,234,0.78)', lineHeight: 1.5 }}>{f}</span>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ ...css.label, color: 'rgba(255,248,234,0.35)', fontSize: 9, marginBottom: 10 }}>Plataformas</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {platforms.map((p, i) => (
              <span key={i} style={{
                fontSize: 11, padding: '4px 12px',
                background: p.active ? `${accent}20` : 'rgba(255,255,255,0.05)',
                border: `1px solid ${p.active ? `${accent}50` : 'rgba(255,255,255,0.08)'}`,
                color: p.active ? accent : 'rgba(255,248,234,0.3)',
                fontWeight: p.active ? 600 : 400,
              }}>
                {p.name}
              </span>
            ))}
          </div>
        </div>

        <div style={{
          marginTop: 20, padding: '14px 16px',
          background: 'rgba(255,255,255,0.04)',
          borderLeft: `2px solid ${accent}`,
          fontSize: 12, color: 'rgba(255,248,234,0.6)',
        }}>
          <span style={{ fontWeight: 700, color: accent }}>Onboarding: {onboarding}</span>
          {extras && <span style={{ marginLeft: 8 }}>— {extras}</span>}
        </div>

        {isSelected && (
          <div style={{
            marginTop: 16, textAlign: 'center',
            ...css.label, color: accent, fontSize: 10, letterSpacing: '0.3em',
          }}>
            ← SELECCIONADO →
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── KPI bar ───────────────────────────────────────────────────────── */
function KpiBar({ label, current, d30, d60, d90, unit = '', accent = C.gold }) {
  const [ref, visible] = useReveal();
  const maxVal = Math.max(parseInt(d90.replace(/[^0-9]/g, '')) || 100, parseInt(current.replace(/[^0-9]/g, '')) || 0) * 1.05;
  const pct = (val) => Math.min(100, ((parseInt(val.replace(/[^0-9]/g, '')) || 0) / maxVal) * 100);

  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.7s ease, transform 0.7s ease',
      padding: '22px 24px',
      background: 'linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.055))',
      border: '1px solid rgba(255,255,255,0.16)',
      borderRadius: 20,
      backdropFilter: 'blur(24px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 22px 70px rgba(15,28,46,0.3)',
      overflow: 'hidden',
      position: 'relative',
    }}>
      <div style={{
        position: 'absolute', inset: '-40% -20% auto auto', width: 160, height: 160,
        background: `radial-gradient(circle, ${accent}28 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 15, color: 'rgba(247,250,252,0.84)', fontWeight: 700 }}>{label}</span>
        <span style={{ ...css.number, fontSize: 28, color: accent }}>{d90}{unit}</span>
      </div>
      <div style={{
        position: 'relative', height: 10,
        background: 'rgba(255,255,255,0.11)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 999,
        marginBottom: 14,
        overflow: 'hidden',
      }}>
        {/* Current */}
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct(current)}%`, background: 'rgba(255,255,255,0.16)', transition: `width 1.2s ease ${visible ? '0.2s' : '0'}` }} />
        {/* D90 target */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: visible ? `${pct(d90)}%` : '0%',
          background: `linear-gradient(90deg, ${accent}80, ${accent})`,
          boxShadow: `0 0 24px ${accent}80`,
          borderRadius: 999,
          transition: `width 1.4s cubic-bezier(0.22,1,0.36,1) ${visible ? '0.4s' : '0'}`,
        }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        {[{ l: 'Hoy', v: current, c: 'rgba(247,250,252,0.55)' }, { l: 'Día 30', v: d30, c: C.verde }, { l: 'Día 60', v: d60, c: accent }, { l: 'Día 90', v: d90, c: accent }].map((item, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ ...css.label, fontSize: 9, color: 'rgba(247,250,252,0.42)' }}>{item.l}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: item.c }}>{item.v}{unit}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Phase card ─────────────────────────────────────────────────────── */
function PhaseCard({ phase, days, accent, items, targets }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      background: 'rgba(255,255,255,0.025)', border: `1px solid ${accent}30`,
      padding: '40px 32px', position: 'relative', overflow: 'hidden',
      opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(28px)',
      transition: 'opacity 0.75s ease, transform 0.75s ease',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${accent}, ${accent}60)` }} />
      <div style={{ ...css.number, fontSize: 72, color: accent, opacity: 0.12, position: 'absolute', top: 16, right: 24, lineHeight: 1 }}>{phase}</div>
      <div style={{ ...css.eyebrow, color: accent, marginBottom: 8 }}>Fase {phase}</div>
      <h3 style={{ ...css.display, fontSize: 22, color: C.cream, margin: '0 0 6px' }}>{days}</h3>
      <div style={{ borderTop: `1px solid rgba(255,255,255,0.07)`, paddingTop: 20, marginTop: 20 }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
            <span style={{ color: accent, fontSize: 12, marginTop: 3, flexShrink: 0 }}>▸</span>
            <span style={{ fontSize: 13, color: 'rgba(255,248,234,0.72)', lineHeight: 1.55 }}>{item}</span>
          </div>
        ))}
      </div>
      {targets && (
        <div style={{ marginTop: 20, padding: '14px 16px', background: `${accent}0D`, border: `1px solid ${accent}20` }}>
          <div style={{ ...css.label, color: accent, fontSize: 9, marginBottom: 8 }}>Metas</div>
          {targets.map((t, i) => (
            <div key={i} style={{ fontSize: 12, color: 'rgba(255,248,234,0.6)', lineHeight: 1.5 }}>{t}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Taco separator ─────────────────────────────────────────────────── */
function TacoSeparator({ color = C.magenta }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '0 0 12px' }}>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${color}40)` }} />
      <span style={{ ...css.label, fontSize: 9, color, opacity: 0.72 }}>La Gacha</span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${color}40, transparent)` }} />
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────── */
export default function LaGachaTaqueria({ deckToken }) {
  // The PDF sits in the same secret folder as the page; the folder name comes from the link, never from this file.
  const PLAN_PDF_URL = `/clients/${deckToken}/LaGacha_Propuesta_MikelMedia.pdf`;
  const [selectedPkg, setSelectedPkg] = useState('impulso');

  /* Download handler */
  const downloadPlan = useCallback(async (event) => {
    event.preventDefault();
    try {
      const response = await fetch(PLAN_PDF_URL);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = PLAN_PDF_FILENAME;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(PLAN_PDF_URL, '_blank', 'noopener,noreferrer');
    }
  }, [PLAN_PDF_URL]);

  /* noindex + page view */
  useEffect(() => {
    let metaRobots = document.querySelector('meta[name="robots"]');
    const created = !metaRobots;
    if (!metaRobots) { metaRobots = document.createElement('meta'); metaRobots.name = 'robots'; document.head.appendChild(metaRobots); }
    const prevContent = metaRobots.content;
    metaRobots.content = 'noindex,nofollow';
    const prevTitle = document.title;
    document.title = 'La Gacha Taqueria Mexicana — Propuesta Digital · Mikel Media';

    axios.post('/api/page-view', {
      page: 'la-gacha-taqueria',
      referrer: document.referrer,
      user_agent: navigator.userAgent,
    }).catch(() => {});

    return () => {
      if (created) document.head.removeChild(metaRobots);
      else metaRobots.content = prevContent;
      document.title = prevTitle;
    };
  }, []);

  /* ─── Packages data ─────────────────────────────────────────────── */
  const packages = [
    {
      id: 'presencia', name: 'Presencia', price: '650', monthlyTag: 'mes',
      accent: C.verde, recommended: false,
      features: [
        '4 Reels producidos (1 por semana)',
        '15 fotos profesionales editadas',
        '5–7 posts por semana',
        'Stories diarios',
        'Diseño digital (flyers, menú QR)',
        'Gestión de Meta Ads',
        'Consultoría mensual + check-ins semanales',
      ],
      platforms: [
        { name: 'Instagram', active: true },
        { name: 'Facebook', active: true },
        { name: 'TikTok', active: false },
        { name: 'Google', active: false },
      ],
      onboarding: '$475 (estándar)',
      extras: 'Setup completo, bio rewrite, QR, Highlights',
    },
    {
      id: 'impulso', name: 'Impulso', price: '797', monthlyTag: 'mes',
      accent: C.magenta, recommended: true,
      features: [
        '8–12 Reels producidos (2–3 por semana)',
        '20 fotos profesionales editadas',
        '7–10 posts por semana',
        'Stories diarios + campañas',
        'Diseño digital (flyers, menú QR)',
        'Gestión de Meta Ads + Google Ads',
        'Consultoría mensual + check-ins semanales',
      ],
      platforms: [
        { name: 'Instagram', active: true },
        { name: 'Facebook', active: true },
        { name: 'TikTok', active: true },
        { name: 'Google Business', active: true },
      ],
      onboarding: '$250 ESPECIAL ⭐',
      extras: 'Ahorra $225 vs. Presencia',
    },
    {
      id: 'dominante', name: 'Dominante', price: '997', monthlyTag: 'mes',
      accent: C.gold, recommended: false,
      features: [
        'Hasta 20 Reels (hasta 5 por semana)',
        '30 fotos profesionales editadas',
        'Contenido diario en todas las plataformas',
        'Stories diarios + campañas especiales',
        'Diseño de campañas, cartas, menú QR',
        'Gestión de Meta Ads + Google Ads',
        'Estrategia completa de crecimiento de marca',
      ],
      platforms: [
        { name: 'Instagram', active: true },
        { name: 'Facebook', active: true },
        { name: 'TikTok', active: true },
        { name: 'Google Business', active: true },
      ],
      onboarding: '$250 ESPECIAL ⭐',
      extras: 'La Gacha lidera el mercado digital de PR',
    },
  ];

  /* ─── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="lg-page" style={css.page}>
      <style>{responsiveCss}</style>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  HERO                                                         */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <ShaderShowcase />


      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  EL DIAGNÓSTICO                                               */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <Section
        id="diagnostico"
        bg={C.bg}
        style={{
          color: C.cream,
          background:
            `radial-gradient(circle at 15% 12%, ${C.magenta}36 0%, transparent 34%),
             radial-gradient(circle at 85% 18%, ${C.masa}28 0%, transparent 30%),
             radial-gradient(circle at 76% 88%, ${C.cilantro}24 0%, transparent 30%),
             linear-gradient(180deg, ${C.bg} 0%, ${C.bgWarm} 60%, ${C.salsa} 180%)`,
        }}
      >
        <Reveal>
          <div style={{ ...css.eyebrow, color: C.masa, marginBottom: 16 }}>El Diagnóstico Digital</div>
          <h2 style={{
            ...css.display, fontSize: 'clamp(34px, 4.5vw, 64px)',
            color: C.cream, margin: '0 0 12px', lineHeight: 1.05,
          }}>
            La Gacha tiene el producto.<br />
            <span style={{ color: C.masa }}>La presencia digital no lo refleja.</span>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(247,250,252,0.72)', maxWidth: 620, lineHeight: 1.75, marginBottom: 56 }}>
            Un video de <strong>@comeconjuan</strong> sobre La Gacha acumuló{' '}
            <strong style={{ color: C.masa }}>6,100+ likes y 63 comentarios en TikTok</strong> —
            sin que La Gacha tenga cuenta. Alguien más está contando tu historia.
          </p>
        </Reveal>

        {/* Platform grid */}
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Instagram"
            value="9,592"
            delta="base real"
            trend="up"
            caption="Sin Reels estratégicos, link-in-bio ni bio optimizada"
            tone="success"
            size="lg"
            icon={<Instagram className="h-4 w-4 text-[#2F7D4E]" />}
            className="border border-white/15 bg-white/[0.09] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_24px_80px_rgba(15,28,46,0.34)] backdrop-blur-2xl ring-1 ring-white/15 [&>div>div>div:first-child]:text-white/72 [&>div>div>div:nth-child(2)]:text-white [&>div>div>div:nth-child(3)]:text-white/62"
          />
          <KpiCard
            label="Facebook"
            value="685"
            delta="categoría mal"
            trend="down"
            caption="Figura como Grocery Store y pierde descubrimiento local"
            tone="warning"
            size="lg"
            icon={<Facebook className="h-4 w-4 text-[#F2785C]" />}
            className="border border-white/15 bg-white/[0.09] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_24px_80px_rgba(15,28,46,0.34)] backdrop-blur-2xl ring-1 ring-white/15 [&>div>div>div:first-child]:text-white/72 [&>div>div>div:nth-child(2)]:text-white [&>div>div>div:nth-child(3)]:text-white/62"
          />
          <KpiCard
            label="TikTok"
            value="0"
            delta="urgente"
            trend="down"
            caption="@comeconjuan ya validó el formato con 6,100+ likes"
            tone="danger"
            size="lg"
            icon={<Video className="h-4 w-4 text-[#F2785C]" />}
            className="border border-white/15 bg-white/[0.09] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_24px_80px_rgba(15,28,46,0.34)] backdrop-blur-2xl ring-1 ring-white/15 [&>div>div>div:first-child]:text-white/72 [&>div>div>div:nth-child(2)]:text-white [&>div>div>div:nth-child(3)]:text-white/62"
          />
          <KpiCard
            label="Google Business"
            value="6"
            delta="crítico"
            trend="down"
            caption="Acapulco Taqueria tiene 320+ reseñas; el gap afecta ranking"
            tone="primary"
            size="lg"
            icon={<Search className="h-4 w-4 text-[#F7E7C6]" />}
            className="border border-white/15 bg-white/[0.09] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_24px_80px_rgba(15,28,46,0.34)] backdrop-blur-2xl ring-1 ring-white/15 [&>div>div>div:first-child]:text-white/72 [&>div>div>div:nth-child(2)]:text-white [&>div>div>div:nth-child(3)]:text-white/62"
          />
        </div>

        {/* Viral callout */}
        <Reveal delay={0.15} style={{ marginTop: 48 }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.13), rgba(255,255,255,0.05))',
            border: '1px solid rgba(255,255,255,0.18)',
            backdropFilter: 'blur(24px)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22), 0 26px 80px rgba(15,28,46,0.32)',
            padding: '36px 44px',
            display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <div style={{ ...css.number, fontSize: 80, color: C.masa, lineHeight: 1, flexShrink: 0 }}>6.1K</div>
            <div>
              <div style={{ ...css.eyebrow, color: C.orange, marginBottom: 8 }}>Ya es viral — sin ti</div>
              <p style={{ fontSize: 16, color: 'rgba(247,250,252,0.72)', margin: 0, lineHeight: 1.65, maxWidth: 520 }}>
                <strong>@comeconjuan</strong> publicó sobre La Gacha. El video explota en likes y comentarios.
                La Gacha no tiene cuenta de TikTok para capturar esa audiencia.
                <strong style={{ color: C.masa }}> Cada día sin TikTok es audiencia que se va a otro.</strong>
              </p>
            </div>
          </div>
        </Reveal>
      </Section>


      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  LA OPORTUNIDAD                                               */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <Section
        id="oportunidad"
        bg={C.bg}
        style={{
          background:
            `linear-gradient(180deg, ${C.bg} 0%, ${C.bgSection} 100%),
             radial-gradient(circle at 90% 20%, ${C.cilantro}22 0%, transparent 28%)`,
        }}
      >
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>La Oportunidad</div>
          <h2 style={{ ...css.display, fontSize: 'clamp(34px, 4.5vw, 60px)', color: C.cream, margin: '0 0 12px', lineHeight: 1.05 }}>
            Ninguna taquería en Puerto Rico<br />
            <span style={{ color: C.gold }}>domina el espacio digital.</span>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,248,234,0.66)', maxWidth: 580, lineHeight: 1.75, marginBottom: 56 }}>
            El espacio está abierto. El producto es extraordinario. Y la historia de Alexis —
            inspirada en su hermana, hecha para sus hijos — es exactamente lo que convierte
            clientes en comunidad.
          </p>
        </Reveal>

        <div className="lg-opp-grid">
          <OppCard
            icon="01" tag="TikTok — Urgente" accent={C.magenta}
            title="El trompo en el aire hace viral cualquier taquería"
            body="El al pastor spinning en TikTok es uno de los formatos de comida más virales en español. Ningún competidor en PR lo está haciendo. Entrar ahora es liderar un espacio vacío."
          />
          <OppCard
            icon="02" tag="Google Reviews — Crítico" accent={C.masa}
            title="40 reseñas cambia el ranking de búsqueda local por completo"
            body="Con 6 reseñas, La Gacha no aparece en búsquedas relevantes. Con 40+, la posición cambia drásticamente. Un QR code en la bolsa de delivery y en el mostrador lo resuelve en 90 días."
          />
          <OppCard
            icon="03" tag="Contenido Auténtico" accent={C.verde}
            title="La historia de Alexis es oro que nadie está usando"
            body="Inspirado por su hermana, hecho para sus hijos. Esa narrativa diferencia a La Gacha de cualquier cadena. Fotos y videos reales del trompo, del equipo y de Alexis cuentan lo que ningún competidor puede copiar."
          />
        </div>

        {/* @comeconjuan proof */}
        <Reveal delay={0.1} style={{ marginTop: 48 }}>
          <div style={{
            padding: '28px 36px',
            background: 'linear-gradient(135deg, rgba(247,231,198,0.12), rgba(78,191,191,0.06))',
            border: '1px solid rgba(247,231,198,0.22)',
            display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
          }}>
            <span style={{ ...css.label, color: C.masa }}>Prueba Social Existente</span>
            <span style={{ fontSize: 14, color: 'rgba(255,248,234,0.75)', lineHeight: 1.6 }}>
              El video de @comeconjuan ya validó que el contenido funciona.
              Con una cuenta de TikTok activa, ese tráfico aterriza en La Gacha — no en ningún otro.
            </span>
          </div>
        </Reveal>
      </Section>


      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  HOY vs. MIKEL MEDIA                                          */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <Section
        id="comparacion"
        bg={C.bgWarm}
        style={{
          background:
            `radial-gradient(circle at 12% 18%, ${C.salsa}2B 0%, transparent 32%),
             linear-gradient(180deg, ${C.bgWarm} 0%, ${C.bgSection} 100%)`,
        }}
      >
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>Hoy vs. Con Mikel Media</div>
          <h2 style={{ ...css.display, fontSize: 'clamp(34px, 4.5vw, 60px)', color: C.cream, margin: '0 0 48px', lineHeight: 1.05 }}>
            Por lo que pagas <span style={{ color: C.orange }}>hoy ($700)</span>,<br />
            mereces mucho más.
          </h2>
        </Reveal>

        <div className="lg-compare-grid" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Column headers */}
          <div style={{
            padding: '24px 40px',
            background: 'rgba(232,98,26,0.08)',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            borderRight: '1px solid rgba(255,255,255,0.07)',
          }}>
            <div style={{ ...css.display, fontSize: 22, color: C.orange }}>Hoy pagas $700 por…</div>
          </div>
          <div style={{
            padding: '24px 40px',
            background: `linear-gradient(90deg, ${C.magenta}12, transparent)`,
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}>
            <div style={{ ...css.display, fontSize: 22, color: C.magenta }}>Con Mikel Media…</div>
          </div>

          {[
            { bad: 'Contenido generado por IA', good: 'Fotos profesionales reales' },
            { bad: 'Sin sesiones de fotos reales', good: 'Reels filmados y editados al 100%' },
            { bad: 'Sin videos profesionales', good: 'TikTok + Instagram + Facebook' },
            { bad: 'Sin estrategia de TikTok', good: 'Google Business activo' },
            { bad: 'Sin gestión de Google Business', good: 'Meta Ads + Google Ads' },
            { bad: 'Sin publicidad digital activa', good: 'Diseño de flyers, cartas, menú QR' },
            { bad: 'Volumen sin alma, sin historia', good: 'Estrategia + consultoría mensual' },
          ].map((row, i) => (
            <div key={i} style={{ display: 'contents' }}>
              <div style={{
                padding: '18px 40px',
                background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                borderBottom: i < 6 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                borderRight: '1px solid rgba(255,255,255,0.07)',
                fontSize: 14, color: 'rgba(255,248,234,0.55)', lineHeight: 1.5,
              }}>
                <span style={{ color: C.orange, marginRight: 10 }}>Hoy</span>{row.bad}
              </div>
              <div style={{
                padding: '18px 40px',
                background: i % 2 === 0 ? `${C.magenta}06` : 'rgba(255,255,255,0.015)',
                borderBottom: i < 6 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                fontSize: 14, color: C.cream, fontWeight: 600, lineHeight: 1.5,
              }}>
                <span style={{ color: C.magenta, marginRight: 10 }}>MM</span>{row.good}
              </div>
            </div>
          ))}
        </div>

        <Reveal delay={0.1} style={{ marginTop: 36 }}>
          <div style={{
            padding: '24px 36px',
            background: `${C.magenta}0D`,
            border: `1px solid ${C.magenta}30`,
            textAlign: 'center',
          }}>
            <span style={{ ...css.display, fontSize: 24, color: C.cream }}>
              Calidad sobre Cantidad.{' '}
            </span>
            <span style={{ fontSize: 16, color: 'rgba(255,248,234,0.65)', lineHeight: 1.65 }}>
              No publicamos por publicar. Publicamos para que cada pieza cuente, conecte, y convierta.
            </span>
          </div>
        </Reveal>
      </Section>


      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  PAQUETES DE SERVICIO                                         */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <Section
        id="paquetes"
        bg={C.bg}
        style={{
          background:
            `radial-gradient(circle at 88% 16%, ${C.masa}14 0%, transparent 30%),
             linear-gradient(180deg, ${C.bg} 0%, ${C.bgSection} 100%)`,
        }}
      >
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>Paquetes de Servicio</div>
          <h2 style={{ ...css.display, fontSize: 'clamp(34px, 4.5vw, 60px)', color: C.cream, margin: '0 0 12px', lineHeight: 1.05 }}>
            Escoge tu nivel.<br />
            <span style={{ color: C.masa }}>Nosotros ejecutamos.</span>
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,248,234,0.6)', maxWidth: 520, lineHeight: 1.75, marginBottom: 16 }}>
            Todos los paquetes incluyen producción completa — filmado, editado y publicado por Mikel Media.
            Tú no tocas nada.
          </p>
          <p style={{ fontSize: 13, color: 'rgba(255,248,234,0.45)', marginBottom: 48 }}>
            Haz clic en un paquete para seleccionarlo.
          </p>
        </Reveal>

        <div className="dark flex flex-col md:flex-row gap-8 md:gap-6 justify-center items-stretch w-full max-w-5xl mx-auto text-foreground">
          {packages.map((pkg) => (
            <PricingCard
              key={pkg.id}
              planName={pkg.name}
              description={
                pkg.id === 'presencia'
                  ? 'Base profesional para organizar Instagram y Facebook con contenido real.'
                  : pkg.id === 'impulso'
                    ? 'La recomendación para convertir atención local en crecimiento medible.'
                    : 'Producción intensiva para dominar todas las plataformas principales.'
              }
              price={pkg.price}
              priceSuffix="/mes"
              features={[
                ...pkg.features.slice(0, 5),
                `Plataformas: ${pkg.platforms.filter(p => p.active).map(p => p.name).join(', ')}`,
              ]}
              footerNote={
                <>
                  <strong>Onboarding: {pkg.onboarding}</strong>
                  {pkg.extras && <span> — {pkg.extras}</span>}
                </>
              }
              buttonText={selectedPkg === pkg.id ? 'Seleccionado' : 'Seleccionar'}
              isPopular={pkg.recommended}
              isSelected={selectedPkg === pkg.id}
              badgeText="Recomendado"
              buttonVariant={pkg.recommended ? 'primary' : 'secondary'}
              onButtonClick={() => setSelectedPkg(pkg.id)}
            />
          ))}
        </div>

        {/* Selected package CTA */}
        <Reveal delay={0.1} style={{ marginTop: 40 }}>
          <div style={{
            padding: '28px 36px',
            background: `${packages.find(p => p.id === selectedPkg)?.accent}0E`,
            border: `1px solid ${packages.find(p => p.id === selectedPkg)?.accent}35`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20,
          }}>
            <div>
              <div style={{ ...css.display, fontSize: 20, color: C.cream }}>
                Has seleccionado: <span style={{ color: packages.find(p => p.id === selectedPkg)?.accent }}>
                  {packages.find(p => p.id === selectedPkg)?.name} (${packages.find(p => p.id === selectedPkg)?.price}/mes)
                </span>
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,248,234,0.5)', marginTop: 4 }}>
                Onboarding: {packages.find(p => p.id === selectedPkg)?.onboarding}
              </div>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank" rel="noreferrer"
              className="lg-button"
              style={{
                padding: '16px 36px',
                background: packages.find(p => p.id === selectedPkg)?.accent,
                color: C.bg, fontWeight: 700, fontSize: 13,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                textDecoration: 'none', cursor: 'pointer',
                transition: 'transform 0.2s, opacity 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; }}
            >
              Hablemos por WhatsApp
            </a>
          </div>
        </Reveal>

        {/* Ad spend note */}
        <Reveal delay={0.15} style={{ marginTop: 24 }}>
          <div style={{
            padding: '20px 28px',
            background: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', gap: 16, alignItems: 'flex-start',
          }}>
            <span style={{ color: C.gold, fontSize: 18, flexShrink: 0, marginTop: 2 }}>⚠</span>
            <div>
              <span style={{ ...css.label, color: C.gold, fontSize: 10 }}>Ad Spend — Presupuesto Separado</span>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'rgba(255,248,234,0.6)', lineHeight: 1.65 }}>
                El ad spend es un presupuesto <strong>adicional</strong> al servicio mensual, pagado directamente
                por el cliente a las plataformas. Recomendado: Meta Ads ~$200/mes · Google Ads ~$250/mes.
                Mikel Media gestiona todas las campañas.
              </p>
            </div>
          </div>
        </Reveal>
      </Section>


      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  PLAN 30-60-90 DÍAS                                           */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <Section
        id="plan"
        bg={C.bgSection}
        style={{
          background:
            `radial-gradient(circle at 16% 10%, ${C.masa}16 0%, transparent 28%),
             linear-gradient(180deg, ${C.bgSection} 0%, ${C.bgWarm} 100%)`,
        }}
      >
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>Plan de Crecimiento</div>
          <h2 style={{ ...css.display, fontSize: 'clamp(34px, 4.5vw, 60px)', color: C.cream, margin: '0 0 56px', lineHeight: 1.05 }}>
            90 días para transformar<br />
            <span style={{ color: C.gold }}>la presencia digital de La Gacha.</span>
          </h2>
        </Reveal>

        <div className="lg-phase-grid">
          <PhaseCard
            phase="1" days="Días 1–30 · Fundación"
            accent={C.verde}
            items={[
              'Reescribir la bio de Instagram con historia + CTA',
              'Corregir categoría de Facebook a "Mexican Restaurant"',
              'Crear cuenta @lagachataqueriamexicana en TikTok',
              'QR code de Google Reviews para bolsas y mostrador',
              'Filmar el trompo: 5 ángulos, 5 cortes',
              'Publicar primer Reel: pastor al natural, 15 segundos',
              'Stories diarios desde Día 1',
            ]}
            targets={[
              '5+ nuevas reseñas de Google',
              'Primer Reel a 1,000+ vistas',
              'Primeras 100 cuentas TikTok',
            ]}
          />
          <PhaseCard
            phase="2" days="Días 31–60 · Amplificación"
            accent={C.magenta}
            items={[
              'DM a @comeconjuan: invitación + comida gratis',
              'Identificar 3–5 micro-influencers en PR (10K–50K)',
              'Lanzar TikTok con Reels existentes cross-posted',
              'Filmar la historia de Alexis (45–60 seg bilingual)',
              'Crear Highlight "Meet Alexis" en Instagram',
              'Facebook: 5 posts por semana, engagement > 2%',
            ]}
            targets={[
              '+500 seguidores IG (total 10,100+)',
              'TikTok: 500 seguidores',
              '20+ reseñas Google',
            ]}
          />
          <PhaseCard
            phase="3" days="Días 61–90 · Dominancia"
            accent={C.gold}
            items={[
              'Boost del top Reel como Meta Ad ($5–$10/día)',
              'Campaña "Tag a friend": $0 costo, alto alcance',
              'Sesión profesional de fotografía de alimentos',
              'Diseño de identidad visual: colores, tipografía, Highlights',
              'Producción en batch: 30 días de contenido en 1 sesión',
              '"Tuesday Taco Special" como anchor semanal',
            ]}
            targets={[
              'Instagram: 11,500+ seguidores (20% crecimiento)',
              'TikTok: 1,000+ · 1 video a 10,000+ vistas',
              'Google: 40+ reseñas, promedio 4.5★',
              'Facebook: 1,000+ likes',
            ]}
          />
        </div>

        {/* Quick wins box */}
        <Reveal delay={0.1} style={{ marginTop: 48 }}>
          <div style={{
            background: `linear-gradient(135deg, ${C.masa}16, ${C.orange}0F)`,
            border: `1px solid ${C.masa}34`,
            padding: '36px 44px',
          }}>
            <div style={{ ...css.eyebrow, color: C.masa, marginBottom: 16 }}>Quick Wins — Esta Semana</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px 40px' }}>
              {[
                'Reescribir la bio de Instagram con CTA y link-in-bio',
                'Corregir categoría de Facebook (Grocery Store → Mexican Restaurant)',
                'Filmar 3 clips del trompo — sin editar, solo capturar',
                'Crear la cuenta de TikTok y hacer cross-post del mejor Reel',
                'Imprimir QR code de Google Reviews para cada bolsa y el mostrador',
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ ...css.number, fontSize: 22, color: C.masa, lineHeight: 1.1, flexShrink: 0 }}>{i + 1}</span>
                  <span style={{ fontSize: 14, color: 'rgba(255,248,234,0.78)', lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </Section>


      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  KPI DASHBOARD                                                */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <Section
        id="kpis"
        bg={C.bg}
        style={{
          color: C.cream,
          background:
            `radial-gradient(circle at 18% 18%, ${C.masa}22 0%, transparent 32%),
             radial-gradient(circle at 82% 12%, ${C.magenta}2E 0%, transparent 30%),
             radial-gradient(circle at 78% 86%, ${C.salsa}3A 0%, transparent 34%),
             linear-gradient(180deg, ${C.bgSection} 0%, ${C.bg} 100%)`,
        }}
      >
        <div className="lg-two-col">
          <Reveal>
            <div style={{ ...css.eyebrow, color: C.masa, marginBottom: 16 }}>KPI Dashboard</div>
            <h2 style={{ ...css.display, fontSize: 'clamp(34px, 4vw, 54px)', color: C.cream, margin: '0 0 20px', lineHeight: 1.05 }}>
              Métricas que medimos<br />
              <span style={{ color: C.masa }}>semana a semana.</span>
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(247,250,252,0.7)', lineHeight: 1.75, maxWidth: 360 }}>
              Las barras muestran el progreso desde hoy hasta el objetivo del Día 90.
              Cada decisión de escalar o pivotar sale de estos números.
            </p>
          </Reveal>

          <div className="lg-kpi-grid">
            {[
              { label: 'Instagram Seguidores', current: '9,592', d30: '9,800', d60: '10,100', d90: '11,500', accent: C.magenta },
              { label: 'TikTok Seguidores', current: '0', d30: '100', d60: '500', d90: '1,000', accent: C.magentaHot },
              { label: 'Google Reviews', current: '6', d30: '15', d60: '25', d90: '40', accent: C.masa, unit: '+' },
              { label: 'Facebook Likes', current: '685', d30: '700', d60: '850', d90: '1,000', accent: C.verde },
            ].map((kpi, i) => (
              <KpiBar key={i} {...kpi} />
            ))}
          </div>
        </div>

        {/* Uber Eats note */}
        <Reveal delay={0.1} style={{ marginTop: 48 }}>
          <TacoSeparator color={C.masa} />
          <div className="grid gap-5 md:grid-cols-3" style={{ marginTop: 24 }}>
            <KpiCard
              label="Uber Eats Rating"
              value="3.8★"
              delta="+0.4 objetivo"
              trend="up"
              caption="Meta D90: 4.2★+"
              tone="warning"
              size="lg"
              className="border border-white/15 bg-white/[0.09] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_24px_80px_rgba(15,28,46,0.34)] backdrop-blur-2xl ring-1 ring-white/15 [&>div>div>div:first-child]:text-white/72 [&>div>div>div:nth-child(2)]:text-white [&>div>div>div:nth-child(3)]:text-white/62"
            />
            <KpiCard
              label="Mejor Reel Alcance"
              value="10K+"
              delta="nuevo"
              trend="up"
              caption="Hoy: sin benchmark propio"
              tone="primary"
              size="lg"
              className="border border-white/15 bg-white/[0.09] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_24px_80px_rgba(15,28,46,0.34)] backdrop-blur-2xl ring-1 ring-white/15 [&>div>div>div:first-child]:text-white/72 [&>div>div>div:nth-child(2)]:text-white [&>div>div>div:nth-child(3)]:text-white/62"
            />
            <KpiCard
              label="Google Rating Promedio"
              value="4.5★+"
              delta="+34 reseñas"
              trend="up"
              caption="Hoy: 5★ con solo 6 reseñas"
              tone="success"
              size="lg"
              className="border border-white/15 bg-white/[0.09] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_24px_80px_rgba(15,28,46,0.34)] backdrop-blur-2xl ring-1 ring-white/15 [&>div>div>div:first-child]:text-white/72 [&>div>div>div:nth-child(2)]:text-white [&>div>div>div:nth-child(3)]:text-white/62"
            />
          </div>
        </Reveal>
      </Section>


      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  TÉRMINOS DEL CONTRATO                                        */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <Section id="terminos" bg={C.bg}>
        <div className="lg-two-col">
          <Reveal>
            <div style={{ ...css.eyebrow, marginBottom: 16 }}>Términos del Contrato</div>
            <h2 style={{ ...css.display, fontSize: 'clamp(30px, 3.5vw, 50px)', color: C.cream, margin: '0 0 20px', lineHeight: 1.1 }}>
              Transparente desde<br />
              <span style={{ color: C.gold }}>el primer día.</span>
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(255,248,234,0.6)', lineHeight: 1.75 }}>
              Se requiere firma de contrato antes del inicio de cualquier servicio.
              El proceso es 100% en línea.
            </p>
          </Reveal>

          <div>
            {[
              { icon: '📋', title: 'Duración', desc: '9 meses a partir de la fecha de inicio del servicio.' },
              { icon: '📅', title: 'Cancelación', desc: 'Puedes cancelar con 2 meses de aviso anticipado, independientemente de si el contrato ha vencido.' },
              { icon: '💰', title: 'Cancelación Anticipada', desc: 'Si cancelas antes del período de aviso, se aplica un cargo de 80% del balance restante del contrato.' },
              { icon: '⚡', title: 'Terminación por Mikel Media', desc: 'Mikel Media puede terminar el contrato por: falta de compromiso, retrasos en materiales, falta de pago, o conducta inapropiada.' },
            ].map((term, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div style={{
                  display: 'flex', gap: 20, padding: '22px 0',
                  borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                  alignItems: 'flex-start',
                }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{term.icon}</span>
                  <div>
                    <div style={{ ...css.label, color: C.gold, marginBottom: 6, fontSize: 10 }}>{term.title}</div>
                    <p style={{ margin: 0, fontSize: 14, color: 'rgba(255,248,234,0.72)', lineHeight: 1.65 }}>{term.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>


      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  PRÓXIMOS PASOS                                               */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <Section id="pasos" bg={C.bgWarm}>
        <Reveal>
          <div style={{ ...css.eyebrow, marginBottom: 16 }}>Próximos Pasos</div>
          <h2 style={{ ...css.display, fontSize: 'clamp(34px, 4.5vw, 60px)', color: C.cream, margin: '0 0 56px', lineHeight: 1.05 }}>
            En 5 pasos,<br />
            <span style={{ color: C.magenta }}>La Gacha tiene presencia real.</span>
          </h2>
        </Reveal>

        <div style={{ maxWidth: 760 }}>
          {[
            { n: '01', title: 'Escoge tu paquete', desc: 'Presencia ($650), Impulso ($797), o Dominante ($997) — el que mejor se adapte a tus metas.' },
            { n: '02', title: 'Firmamos el contrato', desc: 'Te enviamos el acuerdo de servicios para revisión y firma digital. Proceso 100% en línea.' },
            { n: '03', title: 'Onboarding en 7 días', desc: 'Mikel Media configura todas tus plataformas, reescribe tu bio, y crea tu menú QR en los primeros 7 días.' },
            { n: '04', title: 'Primera sesión de contenido', desc: 'Coordinamos la primera sesión de fotos y videos de La Gacha — el trompo, el equipo, la historia de Alexis.' },
            { n: '05', title: 'Lanzamiento', desc: 'Publicamos tu primera semana de contenido y activamos las campañas de ads.' },
          ].map((step, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div style={{
                display: 'flex', gap: 32, alignItems: 'flex-start',
                padding: '32px 0',
                borderBottom: i < 4 ? `1px solid rgba(201,0,94,0.12)` : 'none',
              }}>
                <div style={{
                  ...css.number, fontSize: 56, color: C.magenta, lineHeight: 1,
                  flexShrink: 0, opacity: 0.45, minWidth: 72, textAlign: 'right',
                }}>
                  {step.n}
                </div>
                <div>
                  <h3 style={{ ...css.display, fontSize: 22, color: C.cream, margin: '0 0 10px', lineHeight: 1.2 }}>
                    {step.title}
                  </h3>
                  <p style={{ margin: 0, fontSize: 15, color: 'rgba(255,248,234,0.68)', lineHeight: 1.7 }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>


      {/* ══════════════════════════════════════════════════════════════ */}
      {/*  FINAL CTA                                                    */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <div className="lg-final-cta" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Decorative bg glow */}
        <div style={{
          position: 'absolute', top: -100, left: -100, width: 400, height: 400,
          background: `radial-gradient(circle, ${C.magenta}20 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -80, right: 0, width: 300, height: 300,
          background: `radial-gradient(circle, ${C.gold}10 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 580 }}>
          <div style={{ ...css.eyebrow, color: C.magenta, marginBottom: 14 }}>La historia está lista</div>
          <h2 style={{ ...css.display, fontSize: 'clamp(34px, 4vw, 54px)', color: C.cream, margin: '0 0 14px', lineHeight: 1.0 }}>
            La Gacha tiene la historia.<br />
            <span style={{ color: C.gold }}>Nosotros la contamos.</span>
          </h2>
          <p style={{ margin: 0, color: 'rgba(255,248,234,0.6)', lineHeight: 1.75, fontSize: 15 }}>
            El trompo ya es viral, la historia de Alexis ya mueve corazones.
            Solo falta la estrategia y el equipo que lo ejecute.
          </p>
        </div>

        <div className="lg-cta-row" style={{ position: 'relative', zIndex: 1 }}>
          <a
            href={WHATSAPP_URL}
            target="_blank" rel="noreferrer"
            className="lg-button"
            style={{
              padding: '20px 48px',
              background: `linear-gradient(135deg, ${C.magenta}, ${C.magentaHot})`,
              color: C.cream,
              fontFamily: "'Poppins', Verdana, sans-serif",
              fontSize: 14, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'transform 0.25s, box-shadow 0.25s',
              boxShadow: `0 8px 32px ${C.magenta}40`,
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 14px 40px ${C.magenta}60`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 8px 32px ${C.magenta}40`; }}
          >
            Hablemos por WhatsApp
          </a>
          <a
            href={PLAN_PDF_URL}
            download={PLAN_PDF_FILENAME}
            onClick={downloadPlan}
            className="lg-button"
            style={{
              padding: '20px 36px',
              background: 'transparent',
              color: C.gold,
              fontFamily: "'Poppins', Verdana, sans-serif",
              fontSize: 14, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              textDecoration: 'none',
              border: `1px solid rgba(78,191,191,0.45)`,
              cursor: 'pointer',
              transition: 'border-color 0.2s, color 0.2s, transform 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(78,191,191,0.45)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Descargar Propuesta PDF
          </a>
        </div>
      </div>


      {/* ── FOOTER ────────────────────────────────────────────────── */}
      <div className="lg-footer">
        <img src="/logo-light.svg" alt="Mikel Media" style={{ height: 30, opacity: 0.55 }} />
        <div style={{ ...css.label, color: 'rgba(255,248,234,0.3)', fontSize: 9, textAlign: 'center' }}>
          La Gacha Taqueria Mexicana · Av. San Patricio 772, San Juan PR · Propuesta Digital Mayo 2026
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,248,234,0.25)' }}>
          Confidencial · Mikel Media LLC
        </div>
      </div>

    </div>
  );
}
